import React, { useEffect, useState } from "react";
import { api, apiErr } from "../../lib/api";
import { Button } from "../ui/button";
import { Input, Label, Textarea } from "../ui/input";
import { Badge } from "../ui/badge";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "../ui/select";
import { ArrowLeft, FileText, Sparkles, ShieldCheck, Download, ExternalLink, CheckCircle2, AlertTriangle, History, Paperclip } from "lucide-react";
import { toast } from "sonner";

const STATUS_VARIANT = {
  draft: "outline", documents_in_progress: "accent", ready_for_submission: "warning",
  submitted: "accent", letter_of_offer: "success", claim_in_progress: "warning",
  completed: "success", withdrawn: "outline",
};

function ChecklistRow({ item, onUpdate, docs, onAttach }) {
  return (
    <div className="flex items-center gap-3 py-2 border-b border-border/50 flex-wrap" data-testid={`checklist-${item.key}`}>
      <div className="flex-1 min-w-[180px]">
        <div className="text-xs">{item.label} {item.required && <span className="text-amber-400">*</span>}</div>
        {item.note && <div className="text-[10px] text-muted-foreground">{item.note}</div>}
        {item.attachment && (
          <div className="flex items-center gap-1 text-[10px] text-accent mt-0.5" data-testid={`checklist-evidence-${item.key}`}>
            <Paperclip className="h-3 w-3" /> {item.attachment.filename}
          </div>
        )}
      </div>
      <Select value={item.attachment?.document_id || "none"} onValueChange={(v) => onAttach(item.key, v === "none" ? null : v)}>
        <SelectTrigger className="w-44 h-8 text-xs" data-testid={`checklist-attach-${item.key}`}>
          <SelectValue placeholder="Attach evidence" />
        </SelectTrigger>
        <SelectContent className="max-h-64">
          <SelectItem value="none">No evidence file</SelectItem>
          {docs.map((d) => <SelectItem key={d.id} value={d.id}>{d.filename}</SelectItem>)}
        </SelectContent>
      </Select>
      <Select value={item.status} onValueChange={(v) => onUpdate(item.key, v)}>
        <SelectTrigger className="w-36 h-8 text-xs" data-testid={`checklist-status-${item.key}`}><SelectValue /></SelectTrigger>
        <SelectContent>
          <SelectItem value="not_started">Not started</SelectItem>
          <SelectItem value="in_progress">In progress</SelectItem>
          <SelectItem value="complete">Complete</SelectItem>
        </SelectContent>
      </Select>
    </div>
  );
}

function ApplicationDetail({ appId, reference, onBack, onChanged }) {
  const [a, setA] = useState(null);
  const [docs, setDocs] = useState([]);
  const [busy, setBusy] = useState("");
  const [draftEdits, setDraftEdits] = useState({});
  const [showAudit, setShowAudit] = useState(false);
  const [claim, setClaim] = useState({ letter_of_offer_reference: "", claim_deadline: "" });

  const load = async () => {
    const { data } = await api.get(`/grants/applications/${appId}`);
    setA(data);
    setClaim({ letter_of_offer_reference: data.letter_of_offer_reference || "", claim_deadline: data.claim_deadline || "" });
  };
  useEffect(() => { load(); api.get("/documents").then((r) => setDocs(r.data.documents)).catch(() => {}); }, [appId]);
  if (!a) return <div className="text-sm text-muted-foreground p-6">Loading…</div>;

  const refresh = async () => { await load(); onChanged(); };

  const setStatus = async (status) => {
    try { await api.post(`/grants/applications/${a.id}/status`, { status }); toast.success("Status updated"); refresh(); }
    catch (e) { toast.error(apiErr(e)); }
  };
  const updateChecklist = async (key, status) => {
    try { await api.put(`/grants/applications/${a.id}/checklist`, { key, status }); refresh(); }
    catch (e) { toast.error(apiErr(e)); }
  };
  const attachEvidence = async (key, documentId) => {
    try { await api.put(`/grants/applications/${a.id}/checklist/attach`, { key, document_id: documentId }); toast.success(documentId ? "Evidence attached" : "Evidence removed"); refresh(); }
    catch (e) { toast.error(apiErr(e)); }
  };
  const genDraft = async (docType) => {
    setBusy(docType);
    try { await api.post(`/grants/applications/${a.id}/ai/draft`, { doc_type: docType }); toast.success("AI draft generated — review before use"); refresh(); }
    catch (e) { toast.error(apiErr(e)); } finally { setBusy(""); }
  };
  const saveDraft = async (docType) => {
    try { await api.put(`/grants/applications/${a.id}/drafts/${docType}`, { content: draftEdits[docType] }); toast.success("Draft saved"); setDraftEdits((p) => { const n = { ...p }; delete n[docType]; return n; }); refresh(); }
    catch (e) { toast.error(apiErr(e)); }
  };
  const runCompleteness = async () => {
    setBusy("check");
    try { await api.post(`/grants/applications/${a.id}/ai/completeness`); refresh(); }
    catch (e) { toast.error(apiErr(e)); } finally { setBusy(""); }
  };
  const downloadPackage = async () => {
    setBusy("pdf");
    try {
      const r = await api.get(`/grants/applications/${a.id}/package.pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([r.data], { type: "application/pdf" }));
      const el = document.createElement("a");
      el.href = url; el.download = `${a.grant.code}_submission_package.pdf`; el.click();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error(apiErr(e)); } finally { setBusy(""); }
  };
  const saveClaim = async () => {
    try {
      await api.patch(`/grants/applications/${a.id}`, {
        letter_of_offer_reference: claim.letter_of_offer_reference,
        claim_deadline: claim.claim_deadline || null,
      });
      toast.success("Post-award details saved"); refresh();
    } catch (e) { toast.error(apiErr(e)); }
  };

  const channel = a.grant.application_channel || {};
  return (
    <div className="space-y-4" data-testid="grant-application-detail">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-center gap-3">
          <Button size="sm" variant="outline" onClick={onBack} data-testid="app-back"><ArrowLeft className="h-3.5 w-3.5" /></Button>
          <div>
            <h3 className="font-display font-semibold">{a.grant.name}</h3>
            <div className="text-[11px] text-muted-foreground">{a.grant.agency} · required documents {a.checklist_pct}% complete</div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Badge variant={STATUS_VARIANT[a.status] || "outline"} className="uppercase text-[10px]" data-testid="app-status-badge">{a.status_label}</Badge>
          <Select value={a.status} onValueChange={setStatus}>
            <SelectTrigger className="w-52 h-8 text-xs" data-testid="app-status-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              {(reference.app_statuses || []).map((s) => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="grid lg:grid-cols-2 gap-4">
        <div className="space-y-4">
          <div className="card-flat p-4" data-testid="app-checklist-card">
            <h4 className="font-display font-semibold text-sm mb-1">Document checklist</h4>
            <p className="text-[11px] text-muted-foreground mb-2">Derived from the published {a.grant.code} requirements. * = required before submission. Attach evidence from your Document Inbox so everything lives in one place.</p>
            {(a.checklist || []).map((i) => <ChecklistRow key={i.key} item={i} onUpdate={updateChecklist} docs={docs} onAttach={attachEvidence} />)}
          </div>

          <div className="card-flat p-4" data-testid="app-prefill-card">
            <h4 className="font-display font-semibold text-sm mb-2">Pre-filled application data</h4>
            <div className="grid grid-cols-2 gap-y-1.5 text-xs">
              {[["UEN", a.prefill?.uen], ["Sector", a.prefill?.sector], ["Group revenue (S$)", a.prefill?.group_revenue_sgd],
                ["Employees", a.prefill?.group_employees], ["Local shareholding (%)", a.prefill?.local_shareholding_pct],
                ["SSIC code", a.prefill?.ssic_code]].map(([l, v]) => (
                <React.Fragment key={l}>
                  <span className="text-muted-foreground">{l}</span>
                  <span>{v ?? <span className="text-amber-400">[TO CONFIRM]</span>}</span>
                </React.Fragment>
              ))}
            </div>
          </div>

          <div className="card-flat p-4" data-testid="app-handoff-card">
            <h4 className="font-display font-semibold text-sm mb-1">Guided hand-off — {channel.name || "submission channel"}</h4>
            <p className="text-xs text-muted-foreground">{channel.instructions}</p>
            <div className="flex items-center gap-2 mt-3 flex-wrap">
              <Button size="sm" variant="primary" onClick={downloadPackage} disabled={busy === "pdf"} data-testid="download-package">
                <Download className="h-3.5 w-3.5 mr-1" />{busy === "pdf" ? "Preparing…" : "Submission package (PDF)"}
              </Button>
              {channel.url && (
                <a href={channel.url} target="_blank" rel="noreferrer">
                  <Button size="sm" variant="outline" data-testid="open-portal"><ExternalLink className="h-3.5 w-3.5 mr-1" /> Open {channel.type === "bgp" ? "BGP" : "portal"}</Button>
                </a>
              )}
            </div>
          </div>

          <div className="card-flat p-4" data-testid="app-claims-card">
            <h4 className="font-display font-semibold text-sm mb-2">Post-award & claims tracking</h4>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Letter of Offer reference</Label><Input value={claim.letter_of_offer_reference} onChange={(e) => setClaim({ ...claim, letter_of_offer_reference: e.target.value })} data-testid="loo-reference" /></div>
              <div><Label>Claim documentation deadline</Label><Input type="date" value={claim.claim_deadline || ""} onChange={(e) => setClaim({ ...claim, claim_deadline: e.target.value })} data-testid="claim-deadline" /></div>
            </div>
            <p className="text-[11px] text-muted-foreground mt-2">You'll get an in-app reminder when the claim deadline is within 30 days.</p>
            <div className="flex justify-end mt-2"><Button size="sm" variant="outline" onClick={saveClaim} data-testid="save-claim">Save</Button></div>
          </div>
        </div>

        <div className="space-y-4">
          <div className="card-flat p-4" data-testid="app-drafting-card">
            <div className="flex items-center gap-2 mb-1"><Sparkles className="h-4 w-4 text-accent" /><h4 className="font-display font-semibold text-sm">AI document drafting</h4></div>
            <p className="text-[11px] text-muted-foreground mb-2">Drafts are generated from your Lestari ESG data and clearly labelled — they require human review before submission.</p>
            <div className="flex gap-2 flex-wrap">
              {(reference.draft_types || []).map((d) => (
                <Button key={d.value} size="sm" variant="outline" onClick={() => genDraft(d.value)} disabled={busy === d.value} data-testid={`gen-draft-${d.value}`}>
                  <FileText className="h-3.5 w-3.5 mr-1" />{busy === d.value ? "Drafting…" : d.label.split("(")[0].trim()}
                </Button>
              ))}
            </div>
            {Object.entries(a.drafts || {}).map(([dt, d]) => (
              <div key={dt} className="mt-3 border border-border rounded-sm p-3" data-testid={`draft-${dt}`}>
                <div className="flex items-center justify-between flex-wrap gap-2">
                  <div className="text-xs font-semibold">{d.label}</div>
                  <Badge variant="warning" className="text-[9px]">{d.source === "ai+human" ? "AI + human edited" : "AI draft — review required"}</Badge>
                </div>
                <Textarea rows={8} className="mt-2 text-xs font-mono"
                  value={draftEdits[dt] ?? d.content}
                  onChange={(e) => setDraftEdits((p) => ({ ...p, [dt]: e.target.value }))}
                  data-testid={`draft-content-${dt}`} />
                {draftEdits[dt] != null && draftEdits[dt] !== d.content && (
                  <div className="flex justify-end mt-2"><Button size="sm" variant="primary" onClick={() => saveDraft(dt)} data-testid={`save-draft-${dt}`}>Save edit</Button></div>
                )}
              </div>
            ))}
          </div>

          <div className="card-flat p-4" data-testid="app-completeness-card">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2"><ShieldCheck className="h-4 w-4 text-accent" /><h4 className="font-display font-semibold text-sm">Completeness & consistency check</h4></div>
              <Button size="sm" variant="outline" onClick={runCompleteness} disabled={busy === "check"} data-testid="run-completeness">{busy === "check" ? "Checking…" : "Run check"}</Button>
            </div>
            {a.last_completeness && (
              <div className="mt-3 space-y-1.5" data-testid="completeness-findings">
                {a.last_completeness.findings.map((f, i) => (
                  <div key={i} className="flex items-start gap-2 text-xs">
                    {f.level === "gap" ? <AlertTriangle className="h-3.5 w-3.5 text-red-400 mt-0.5 shrink-0" />
                      : f.level === "warn" ? <AlertTriangle className="h-3.5 w-3.5 text-amber-400 mt-0.5 shrink-0" />
                      : <CheckCircle2 className="h-3.5 w-3.5 text-[#00E65B] mt-0.5 shrink-0" />}
                    <span>{f.text}</span>
                  </div>
                ))}
                <div className="text-[10px] text-muted-foreground">Checked {new Date(a.last_completeness.at).toLocaleString()}</div>
              </div>
            )}
          </div>

          <div className="card-flat p-4" data-testid="app-audit-card">
            <button className="flex items-center gap-2 text-sm font-semibold font-display" onClick={() => setShowAudit(!showAudit)} data-testid="toggle-audit">
              <History className="h-4 w-4 text-accent" /> Audit trail ({(a.audit || []).length}) {showAudit ? "▾" : "▸"}
            </button>
            {showAudit && (
              <div className="mt-2 space-y-1 max-h-64 overflow-y-auto">
                {(a.audit || []).slice().reverse().map((e, i) => (
                  <div key={i} className="text-[11px] border-b border-border/40 py-1">
                    <span className="text-muted-foreground">{new Date(e.at).toLocaleString()} · {e.by}</span> — <span className="uppercase text-[9px] tracking-wide">{e.action}</span> {e.detail}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default function GrantApplications({ apps, reference, onChanged }) {
  const [selected, setSelected] = useState(null);
  if (selected) return <ApplicationDetail appId={selected} reference={reference} onBack={() => { setSelected(null); onChanged(); }} onChanged={onChanged} />;
  return (
    <div className="space-y-3" data-testid="grant-applications-list">
      {apps.length === 0 && (
        <div className="card-flat p-8 text-center text-sm text-muted-foreground" data-testid="apps-empty">
          No grant applications yet — start one from an eligible match in Discover & Match.
        </div>
      )}
      {apps.map((a) => (
        <button key={a.id} onClick={() => setSelected(a.id)} data-testid={`grant-app-${a.grant.code}`}
          className="w-full text-left card-flat p-4 hover:bg-surface-hover transition-colors flex items-center gap-4 flex-wrap">
          <div className="flex-1 min-w-[220px]">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="font-display font-semibold text-sm">{a.grant.name}</span>
              <Badge variant={STATUS_VARIANT[a.status] || "outline"} className="text-[9px] uppercase">{a.status_label}</Badge>
            </div>
            <div className="text-[11px] text-muted-foreground mt-0.5">{a.grant.agency} · created {new Date(a.created_at).toLocaleDateString()}</div>
          </div>
          <div className="text-right">
            <div className="text-xs font-mono">{a.checklist_pct}%</div>
            <div className="text-[10px] text-muted-foreground">required docs</div>
          </div>
          {a.claim_deadline && <div className="text-right"><div className="text-xs font-mono text-amber-400">{a.claim_deadline}</div><div className="text-[10px] text-muted-foreground">claim deadline</div></div>}
        </button>
      ))}
    </div>
  );
}
